import { useEffect, useRef, useState } from "react";
import { useUserToken } from "@/features/auth/hooks/useUserToken";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { fetchSEMOdds } from "@/features/betslip/services/semPricing";
import { getCurrentOdds } from "@/features/betslip/utils/selectionHelpers";
import { legSetKey } from "@/features/betslip/utils/rules";

export type SemPrice =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "priced"; odds: number }
  | { status: "unpriceable" };

/**
 * Server-priced combined odds for a prospective SEM — a set of same-event legs
 * sitting in the slip as singles, not yet built into a SEM. Fetches when the
 * leg set changes and re-fetches when a leg's odds drift, holding the last
 * price on screen through a re-price and leaving it untouched if a re-price
 * fails. This is the pre-creation, read-only counterpart to useSemOdds +
 * repriceSemBundle, which keep an already-placed SEM priced. Pass an
 * absent/empty list to disable.
 */
export const useSemPrice = (outcomeIds: string[] | null): SemPrice => {
  const token = useUserToken();
  const enabled = !!outcomeIds && outcomeIds.length > 0;
  const legKey = enabled ? legSetKey(outcomeIds) : null;

  // Fingerprint of the legs' current odds, a pending change winning — the same
  // input the server prices from. A change here means a leg drifted, so the
  // combined price is stale. Primitive selector → re-runs only on a real move.
  const oddsKey = useBetslipStore((state) =>
    (outcomeIds ?? [])
      .map((id) => {
        const selection = state.selections[id];
        return selection ? getCurrentOdds(selection, state.oddsChanges[id]) : 0;
      })
      .join("|"),
  );

  const [price, setPrice] = useState<SemPrice>({ status: "idle" });
  const lastLegKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !legKey || !token || !outcomeIds) {
      setPrice({ status: "idle" });
      lastLegKeyRef.current = null;
      return;
    }
    const isNewLegSet = lastLegKeyRef.current !== legKey;
    lastLegKeyRef.current = legKey;
    let ignore = false;
    // Only blank to loading on a genuinely new leg set; a drift re-price keeps
    // the current price on screen and swaps it in place.
    if (isNewLegSet) setPrice({ status: "loading" });
    fetchSEMOdds({ outcomeIds, token }).then((result) => {
      if (ignore) return;
      if (result.ok && result.data.odds > 1) {
        setPrice({ status: "priced", odds: result.data.odds });
      } else if (isNewLegSet) {
        setPrice({ status: "unpriceable" });
      }
      // A failed re-price on an existing set keeps the last good price.
    });
    return () => {
      ignore = true;
    };
  }, [legKey, oddsKey, token]);

  return price;
};
